import { useState, useEffect } from 'react';
import { Badge, Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppScriptAPI from '../services/appscriptAPI';
import { useUserPermissions } from '../contexts/UserPermissionsContext';
import AppToast from '../components/AppToast';
import { filterLocationData, locationFromUniqueId, canAccessLocation } from '../utils/accessControl';

function Search() {
  const navigate = useNavigate();
  const { userPermissions } = useUserPermissions();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [locationData, setLocationData] = useState({
    provinces: [],
    districts: {},
    divisions: {},
  });

  const [formData, setFormData] = useState({
    provinceCode: '',
    districtCode: '',
    dsdCode: '',
    searchType: 'id', // Changed default to 'id'
    searchValue: '',
  });

  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [searchResult, setSearchResult] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);

  // Load location data on component mount
  useEffect(() => {
    loadLocationData();
  }, []);

  const loadLocationData = async () => {
    try {
      const data = await AppScriptAPI.getLocationData();
      setLocationData(filterLocationData(data, userPermissions));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'Failed to load location data: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  // Auto-detect location from DoSS ID
  const detectLocationFromUniqueId = (uniqueId) => {
    if (!uniqueId || uniqueId.length < 6) {
      return null;
    }


    // Example: 51060000024
    // Province: 5
    // District: 51
    // DS Division: 06
    
    const provinceCode = uniqueId.substring(0, 1); // "5"
    const districtCode = uniqueId.substring(0, 2); // "51"
    const dsdCode = uniqueId.substring(2, 4); // "06"

    // Validate province exists
    const provinceExists = locationData.provinces.find(
      (p) => p.code === provinceCode
    );

    if (!provinceExists) {
      return null;
    }

    // Validate district exists in the province
    const districtExists = locationData.districts[provinceCode]?.find(
      (d) => d.code === districtCode
    );

    if (!districtExists) {
      return null;
    }

    // Validate DS division exists in the district
    const dsdExists = locationData.divisions[districtCode]?.find(
      (div) => div.code === dsdCode
    );

    if (!dsdExists) {
      return null;
    }

    return {
      provinceCode,
      districtCode,
      dsdCode,
    };
  };

  // Handle search value change with auto-detection for DoSS ID
  const handleSearchValueChange = (e) => {
    const value = e.target.value.trim().toUpperCase();
    
    setFormData({
      ...formData,
      searchValue: value,
    });

    // Auto-detect location if searching by DoSS ID
    if (formData.searchType === 'id' && value.length >= 6) {
      const detected = detectLocationFromUniqueId(value);
      
      if (detected) {
        setFormData({
          ...formData,
          searchValue: value,
          provinceCode: detected.provinceCode,
          districtCode: detected.districtCode,
          dsdCode: detected.dsdCode,
        });
        setAutoDetected(true);
        setAlert({
          show: true,
          message: 'Location automatically detected from DoSS ID',
          type: 'success',
        });
        setTimeout(() => {
          setAlert({ show: false, message: '', type: '' });
        }, 3000);
      } else {
        setAutoDetected(false);
      }
    } else {
      setAutoDetected(false);
    }
  };

  const handleProvinceChange = (e) => {
    const provinceCode = e.target.value;
    setFormData({
      ...formData,
      provinceCode: provinceCode,
      districtCode: '',
      dsdCode: '',
    });
    setAutoDetected(false);
  };

  const handleDistrictChange = (e) => {
    const districtCode = e.target.value;
    setFormData({
      ...formData,
      districtCode: districtCode,
      dsdCode: '',
    });
    setAutoDetected(false);
  };

  const handleDsdChange = (e) => {
    const dsdCode = e.target.value;
    setFormData({
      ...formData,
      dsdCode: dsdCode,
    });
    setAutoDetected(false);
  };

  const handleSearchTypeChange = (e) => {
    const newSearchType = e.target.value;
    setFormData({
      ...formData,
      searchType: newSearchType,
      searchValue: '',
      // Reset location fields when switching to NIC search
      ...(newSearchType === 'nic' && {
        provinceCode: '',
        districtCode: '',
        dsdCode: '',
      }),
    });
    setAutoDetected(false);
    setAlert({ show: false, message: '', type: '' });
  };

  const validateNIC = (nic) => {
    const nicPattern = /^([0-9]{9}[VX]|[0-9]{12})$/;
    return nicPattern.test(nic.toUpperCase());
  };

  const validateUniqueId = (id) => {
    // DoSS ID format: 10 digits
    const idPattern = /^[0-9]{10}$/;
    return idPattern.test(id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const searchValue = formData.searchValue.trim().toUpperCase();

    if (!searchValue) {
      setAlert({
        show: true,
        message: 'Please enter a search value',
        type: 'warning',
      });
      return;
    }

    // Validate based on search type
    if (formData.searchType === 'nic') {
      if (!validateNIC(searchValue)) {
        setAlert({
          show: true,
          message:
            'Invalid NIC format. Please enter a valid NIC number (9 digits + V/X or 12 digits).',
          type: 'danger',
        });
        return;
      }
    } else if (formData.searchType === 'id') {
      if (!validateUniqueId(searchValue)) {
        setAlert({
          show: true,
          message:
            'Invalid DoSS ID format. Please enter a valid 10-digit DoSS ID.',
          type: 'danger',
        });
        return;
      }

      // For DoSS ID search, location fields should be auto-populated
      if (!formData.provinceCode || !formData.districtCode || !formData.dsdCode) {
        setAlert({
          show: true,
          message:
            'Unable to detect location from DoSS ID. Please verify the ID is correct.',
          type: 'danger',
        });
        return;
      }
    }

    setSearching(true);
    setAlert({ show: false, message: '', type: '' });

    try {
      const response = await AppScriptAPI.searchRecord({
        provinceCode: formData.provinceCode,
        districtCode: formData.districtCode,
        dsdCode: formData.dsdCode,
        searchType: formData.searchType,
        searchValue: searchValue,
      });

      if (response.success && response.record) {
        // Verify the found record is within the user's allowed locations
        const loc = locationFromUniqueId(response.record.uniqueId);
        if (loc && !canAccessLocation(userPermissions, loc)) {
          setAlert({
            show: true,
            message: 'You do not have permission to access records in this location.',
            type: 'danger',
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setSearching(false);
          return;
        }
        setSearchResult(response.record);
        setNoResults(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setNoResults(true);
        setSearchResult(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSearching(false);
    }
  };

  const handleNewSearch = () => {
    setFormData({
      provinceCode: '',
      districtCode: '',
      dsdCode: '',
      searchType: 'id',
      searchValue: '',
    });
    setSearchResult(null);
    setNoResults(false);
    setAlert({ show: false, message: '', type: '' });
    setAutoDetected(false);
  };

  const handleUpdate = () => {
    if (searchResult && searchResult.uniqueId && searchResult.sheetId) {
      navigate(
        `/update/basic/${searchResult.uniqueId}?sheetId=${searchResult.sheetId}`
      );
    } else {
      setAlert({
        show: true,
        message: 'Unable to update: Missing required record data.',
        type: 'danger',
      });
    }
  };

  const handleRegisterNew = () => {
    navigate('/register');
  };

  const handleViewSummary = () => {
    if (searchResult?.uniqueId && searchResult?.sheetId) {
      navigate(`/summary/${searchResult.uniqueId}?sheetId=${searchResult.sheetId}`);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5" style={{ minHeight: '100vh' }}>
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading form data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5" style={{ minHeight: '100vh' }}>
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <Card className="form-card">
            <Card.Body className="p-4">
              <h2 className="card-title mb-4">Search Records</h2>

              <AppToast
                show={alert.show}
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
              />

              {/* Search Form */}
              {!searchResult && !noResults && (
                <Form onSubmit={handleSubmit}>
                  {/* Search Type Selection - Moved to top */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Search By</Form.Label>
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="radio"
                        id="searchByID"
                        name="searchType"
                        label="DoSS ID"
                        value="id"
                        checked={formData.searchType === 'id'}
                        onChange={handleSearchTypeChange}
                      />
                      <Form.Check
                        type="radio"
                        id="searchByNIC"
                        name="searchType"
                        label="NIC Number"
                        value="nic"
                        checked={formData.searchType === 'nic'}
                        onChange={handleSearchTypeChange}
                      />
                    </div>
                  </Form.Group>

                  {/* Search Value Input */}
                  {formData.searchType === 'id' ? (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">DoSS ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="searchValue"
                        value={formData.searchValue}
                        onChange={handleSearchValueChange}
                        placeholder="Ex. 51060000024"
                        maxLength="10"
                        required
                      />
                      <Form.Text className="text-muted">
                        Enter 10-digit DoSS ID. Location will be detected automatically.
                      </Form.Text>
                    </Form.Group>
                  ) : (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">NIC Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="searchValue"
                        value={formData.searchValue}
                        onChange={handleSearchValueChange}
                        placeholder="Ex. 199512345678 or 951234567V"
                        required
                      />
                      <Form.Text className="text-muted">
                        Enter 12 digits or 9 digits + V/X
                      </Form.Text>
                    </Form.Group>
                  )}

                  <hr className="my-4" />

                  {/* Location Fields */}
                  <div className="mb-3">
                    <h6 className="text-muted mb-3">
                      {formData.searchType === 'id' && autoDetected ? (
                        <span className="text-success">
                          <i className="bi bi-check-circle-fill me-1"></i>Location Auto-Detected
                        </span>
                      ) : (
                        'Location Information'
                      )}
                    </h6>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Province</Form.Label>
                    <Form.Select
                      name="provinceCode"
                      value={formData.provinceCode}
                      onChange={handleProvinceChange}
                      disabled={formData.searchType === 'id' && autoDetected}
                      required
                    >
                      <option value="">Select Province</option>
                      {locationData.provinces.map((province) => (
                        <option key={province.code} value={province.code}>
                          {province.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>District</Form.Label>
                    <Form.Select
                      name="districtCode"
                      value={formData.districtCode}
                      onChange={handleDistrictChange}
                      disabled={
                        !formData.provinceCode ||
                        (formData.searchType === 'id' && autoDetected)
                      }
                      required
                    >
                      <option value="">Select District</option>
                      {formData.provinceCode &&
                        locationData.districts[formData.provinceCode]?.map(
                          (district) => (
                            <option key={district.code} value={district.code}>
                              {district.name}
                            </option>
                          )
                        )}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>DS Division</Form.Label>
                    <Form.Select
                      name="dsdCode"
                      value={formData.dsdCode}
                      onChange={handleDsdChange}
                      disabled={
                        !formData.districtCode ||
                        (formData.searchType === 'id' && autoDetected)
                      }
                      required
                    >
                      <option value="">Select DS Division</option>
                      {formData.districtCode &&
                        locationData.divisions[formData.districtCode]?.map(
                          (division) => (
                            <option key={division.code} value={division.code}>
                              {division.name}
                            </option>
                          )
                        )}
                    </Form.Select>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={searching}
                  >
                    {searching ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Searching...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search"></i> Search
                      </>
                    )}
                  </Button>
                </Form>
              )}

              {/* Search Results */}
              {searchResult && (
                <div>
                  <div className="result-card">
                    <h5 className="mb-3">Record Found</h5>
                    <Row className="mb-2">
                      <Col xs={4} className="fw-bold">
                        DoSS ID:
                      </Col>
                      <Col xs={8}>{searchResult.uniqueId}</Col>
                    </Row>
                    {searchResult.name && (
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold">
                          Name:
                        </Col>
                        <Col xs={8}>
                          {searchResult.name}
                          {searchResult.deceased && (
                            <Badge bg="danger" className="ms-2">Deceased</Badge>
                          )}
                        </Col>
                      </Row>
                    )}
                    {searchResult.nic && (
                      <Row className="mb-2">
                        <Col xs={4} className="fw-bold">
                          NIC:
                        </Col>
                        <Col xs={8}>{searchResult.nic}</Col>
                      </Row>
                    )}
                  </div>
                  <div className="d-grid gap-2 mt-3">
                    <Button variant="primary" onClick={handleViewSummary}>
                      View Personal Summary
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                      Update Details
                    </Button>
                    <Button variant="secondary" onClick={handleNewSearch}>
                      New Search
                    </Button>
                  </div>
                </div>
              )}

              {/* No Results */}
              {noResults && (
                <div>
                  <Alert variant="warning">
                    <strong>No Record Found</strong>
                    <br />
                    No matching record was found in the selected DS Division.
                  </Alert>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={handleRegisterNew}>
                      Register New Person
                    </Button>
                    <Button variant="secondary" onClick={handleNewSearch}>
                      New Search
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Search;